import { MessageCircle, MessageSquareText } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { currencySymbol } from "@/lib/currency";

interface ContactCardProps {
  fullName: string;
  handle: string;
  trialAvailable: boolean;
  offeringsCount: number;
  minPrice?: number | null;
  minPriceCurrency?: string | null;
  /** Called when an authenticated student with completed onboarding clicks Contact */
  onAuthenticatedContact?: () => void;
  /** When set, the student already has an open conversation — replace the contact CTA
   *  with a link to that thread (repeat-contact prevention). */
  existingThreadId?: string | null;
}

export function ContactCard({
  fullName,
  handle,
  trialAvailable,
  offeringsCount,
  minPrice,
  minPriceCurrency,
  onAuthenticatedContact,
  existingThreadId,
}: ContactCardProps) {
  const firstName = (fullName ?? "").split(" ")[0] || "Teacher";

  const handleCTA = (action: "contact", offeringId?: string) => {
    if (action === "contact" && onAuthenticatedContact) {
      onAuthenticatedContact();
      return;
    }
    const raw = sessionStorage.getItem("bmc:search-context");
    const searchCtx = raw ? JSON.parse(raw) : null;
    const context = {
      teacherHandle: handle,
      intendedOfferingId: offeringId ?? null,
      searchQuery: searchCtx?.searchQuery ?? null,
      activeFilters: searchCtx?.activeFilters ?? null,
    };
    localStorage.setItem("bmc:anonymous-search-context", JSON.stringify(context));
    window.dispatchEvent(
      new CustomEvent("bmc:open-registration-gate", {
        detail: { source: "teacher-profile", action },
      })
    );
  };

  const priceSymbol = currencySymbol(minPriceCurrency) || (minPriceCurrency ?? "");

  return (
    <div className="space-y-3">
      {existingThreadId ? (
        <div className="rounded-xl border border-[hsl(237,52%,31%)]/20 bg-[hsl(237,52%,31%)]/5 p-3 text-center space-y-2.5">
          <div className="flex items-center justify-center gap-1.5 text-sm text-[hsl(237,52%,31%)]">
            <MessageSquareText className="w-4 h-4" />
            You already have a conversation with {firstName}
          </div>
          <Button asChild className="w-full gap-2 bg-gradient-to-b from-[hsl(237,52%,40%)] to-[hsl(237,52%,25%)] hover:opacity-90" size="lg">
            <Link to={`/inquiry/${existingThreadId}`}>View conversation</Link>
          </Button>
        </div>
      ) : (
        <Button
          className="w-full gap-2 cursor-pointer bg-gradient-to-b from-indigo-500 to-indigo-700 hover:opacity-90 transition-opacity shadow-sm text-white"
          size="lg"
          onClick={() => handleCTA("contact")}
        >
          <MessageCircle className="w-4 h-4" />
          Contact {firstName}
        </Button>
      )}

      {offeringsCount > 0 && (
        <p className="text-xs text-muted-foreground text-center">
          {offeringsCount} offering{offeringsCount !== 1 ? "s" : ""} available
        </p>
      )}
    </div>
  );
}
